import { useEffect, useRef } from 'react';
import type { TRef, UseIsVisibleOptions } from './useIsVisible';
import { useIsVisible } from './useIsVisible';

export function IsVisible({
  children,
  onChange,
  options,
}: {
  children: React.ReactNode | ((arg: { isVisible: boolean; ref: React.RefObject<TRef> }) => React.ReactNode);
  /**
   * Called on an actual visibility change (and once on mount with the initial visibility).
   * Does NOT need to be referentially stable — pass an inline handler freely; changing its
   * identity between renders will not trigger a call. Fires only when `isVisible` changes.
   */
  onChange?: (isVisible: boolean) => void;
  options?: UseIsVisibleOptions;
}) {
  const { ref, isVisible } = useIsVisible(options);

  // Ref the latest onChange so the effect can depend on `isVisible` alone (see the onChange prop
  // doc) — otherwise an inline handler's changing identity would re-fire the effect every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    onChangeRef.current?.(isVisible);
  }, [isVisible]);

  if (typeof children === 'function') {
    return <>{children({ isVisible, ref })}</>;
  } else {
    return <div ref={ref}>{children}</div>;
  }
}
